import { useEffect, useRef, useState } from "react";
import actions from "../../actions";
import { useDispatch, useSelector } from "react-redux";
import { isEqual, parseInt } from "lodash";
import { getAppliedListFilterCount, getAppliedWildCardFilterCount } from "../../../../utils/filtersUtils";
import AssetAction from "../../../assets/actions";
import qs from "query-string";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { isAppliedWildcardFilterEntry } from "../../../../utils/wildCardFilterUtils";

export const AI_FIELDS_BAND = "AI Fields";

const VOICE_FILTER_KEYS = ["voice_text", "voice_text_mobile", "has_voice_text", "has_voice_text_mobile"];
const HAS_VOICE_KEYS = ["has_voice_text", "has_voice_text_mobile"];
const VOICE_PARAM_TO_ADDON = {
    "voice_upload.voice_text": "voice_text",
    "voice_upload.voice_text_mobile": "voice_text_mobile",
    has_voice_text: "has_voice_text",
    has_voice_text_mobile: "has_voice_text_mobile"
};

// Voice transcript keys travel in addon_filters, never in list / filters / jsonb_filters
const splitVoiceFilters = ({ list, filters } = {}) => {
    const addon = {};
    const restList = list ? {} : list;
    Object.entries(list ?? {}).forEach(([key, value]) => {
        const addonKey = VOICE_PARAM_TO_ADDON[key];
        if (!addonKey) restList[key] = value;
        else if (HAS_VOICE_KEYS.includes(addonKey)) {
            const picks = [...new Set((value ?? []).map(String))];
            if (picks.length === 1) addon[addonKey] = picks[0] === "true"; // Yes + No (or nothing) = no constraint
        } else if (value?.length) addon[addonKey] = value;
    });
    const restFilters = filters ? {} : filters;
    Object.entries(filters ?? {}).forEach(([key, value]) => {
        const addonKey = VOICE_PARAM_TO_ADDON[key];
        if (!addonKey) restFilters[key] = value;
        else if (!HAS_VOICE_KEYS.includes(addonKey) && isAppliedWildcardFilterEntry(value)) {
            addon[`${addonKey}_filter`] = { filters: value.filters, key: value.key };
        }
    });
    return { list: restList, filters: restFilters, addon };
};

const buildAddonFilters = (notRejected, voiceAddon = {}) => {
    const addon = { ...(notRejected ? { not_rejected: true } : {}), ...(voiceAddon ?? {}) };
    return Object.keys(addon).length ? { addon_filters: addon } : {};
};

const getAiFieldFilterKey = fieldName =>
    fieldName === "analyze_status" ? `survey_recommendations.${fieldName}` : `ai_analyze_images.ai_flat_json.${fieldName}`;

const getAiFilterFields = (aiFieldsConfig = []) =>
    (Array.isArray(aiFieldsConfig) ? aiFieldsConfig : [])
        .filter(field => field?.field_name)
        .map(field => {
            const fieldName = field.field_name;
            const filterKey = getAiFieldFilterKey(fieldName);
            const isDateField = ["date", "date_time"].includes(field.field_type);
            return {
                label: field.label || fieldName,
                key: `ai_${fieldName}`,
                paramKey: filterKey,
                band: AI_FIELDS_BAND,
                getListKey: filterKey,
                searchKey: filterKey,
                // Dates go straight to the wildcard date picker (as in Additional Fields); everything else gets a
                // dropdown, with a wildcard/text search alongside it for non-boolean types
                isWildCardFilter: isDateField,
                fieldType: ["select", "custom_select"].includes(field.field_type)
                    ? "select"
                    : isDateField
                      ? "date"
                      : field.field_type === "number"
                        ? "number"
                        : field.field_type || "text",
                hasWildCardFilter: !isDateField && field.field_type !== "boolean",
                isAiField: true
            };
        });

const useMyFilters = ({ currentFilters, submitMasterFilter, onCancel, surveyProjectFilterQuery, recommendationId = {}, asset_id = {} }) => {
    const dispatch = useDispatch();
    const { search } = useLocation();
    const query = qs.parse(search);
    const { favoriteFilters } = useSelector(state => state.assetReducer);
    const [showBands, setShowBands] = useState({});
    const [showDropdown, setShowDropdown] = useState("");
    const [showWildcard, setShowWildcard] = useState("");
    const [filterData, setFilterData] = useState({ filters: { ...(currentFilters.filters || {}) }, ...(currentFilters.list || {}) });
    const [initialFilterData, setInitialFilterData] = useState({ filters: { ...(currentFilters.filters || {}) }, ...(currentFilters.list || {}) });
    const [activeTab, setActiveTab] = useState("main");
    const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
    const [fieldSearch, setFieldSearch] = useState("");
    const [showAppliedFilters, setShowAppliedFilters] = useState(false);
    const [favorites, setFavorites] = useState([]);
    const wrapperRef = useRef();
    const favFilterRef = useRef();
    const [showWildcardField, setWildcardField] = useState(false);
    const [showFavoriteRemoveConfirmation, setFavoriteRemoveConfirmation] = useState(false);
    const [selectedFavoriteItem, setSelectFavoriteItem] = useState("");
    const [filterFields, setFilterFields] = useState([]);
    const { getMobileRecommendationTemplateResponse, masterFilterList, getAiAnalyseKeys, appliedFilterNames } = useSelector(
        state => state.mobileRecommendationsReducer
    );
    const isSurveybasedRecoms = query?.is_assignedRecom === "true";
    const aiFieldsConfig = getAiAnalyseKeys?.data;

    useEffect(() => {
        if (!aiFieldsConfig && !getAiAnalyseKeys?.loading) {
            dispatch(actions.getAiAnalyseKey());
        }
    }, []);
    const additionalFields = [
        {
            label: "Mobile Status",
            key: "status",
            paramKey: "survey_recommendations.status",
            band: "Additional Fields",
            getListKey: "status",
            searchKey: "survey_recommendations.status",
            isWildCardFilter: false,
            fieldType: "select",
            hasWildCardFilter: false,
            hasGroupBy: true
        },
        {
            label: "With/Without New Fields",
            key: "fields",
            paramKey: "has_new_val",
            band: "Additional Fields",
            getListKey: "has_new_val",
            searchKey: "has_new_val",
            isWildCardFilter: false,
            fieldType: "select",
            hasWildCardFilter: false
        },
        {
            label: "Is Converted",
            key: "converted_or_not_converted",
            paramKey: "converted_or_not_converted",
            band: "Additional Fields",
            getListKey: "converted_or_not_converted",
            searchKey: "converted_or_not_converted",
            isWildCardFilter: false,
            fieldType: "select",
            hasWildCardFilter: false
        },
        {
            label: "Source Type",
            key: "survey_recommendations.recommendation_source_type",
            paramKey: "survey_recommendations.recommendation_source_type",
            band: "Additional Fields",
            getListKey: "recommendation_source_type",
            searchKey: "survey_recommendations.recommendation_source_type",
            isWildCardFilter: false,
            fieldType: "select",
            hasWildCardFilter: true
        },
        {
            label: "Surveyor",
            key: "surveyor",
            paramKey: "users.name",
            band: "Additional Fields",
            getListKey: "users.name",
            searchKey: "users.name",
            isWildCardFilter: false,
            fieldType: "select",
            hasWildCardFilter: true
        },
        {
            label: "Resync",
            key: "resync",
            paramKey: "survey_recommendations.resync",
            band: "Additional Fields",
            getListKey: "resync",
            searchKey: "survey_recommendations.resync",
            isWildCardFilter: false,
            fieldType: "select",
            hasWildCardFilter: true
        },
        {
            label: "Voice Transcript",
            key: "voice_text",
            paramKey: "voice_upload.voice_text",
            band: "Additional Fields",
            getListKey: "voice_upload.voice_text",
            searchKey: "voice_upload.voice_text",
            isWildCardFilter: false,
            fieldType: "string",
            hasWildCardFilter: true
        },
        {
            label: "Mobile Voice Transcript",
            key: "voice_text_mobile",
            paramKey: "voice_upload.voice_text_mobile",
            band: "Additional Fields",
            getListKey: "voice_upload.voice_text_mobile",
            searchKey: "voice_upload.voice_text_mobile",
            isWildCardFilter: false,
            fieldType: "string",
            hasWildCardFilter: true
        },
        {
            label: "Has Voice Transcript",
            key: "has_voice_text",
            paramKey: "has_voice_text",
            band: "Additional Fields",
            getListKey: "has_voice_text",
            searchKey: "has_voice_text",
            isWildCardFilter: false,
            fieldType: "select",
            hasWildCardFilter: false
        },
        {
            label: "Has Mobile Voice Transcript",
            key: "has_voice_text_mobile",
            paramKey: "has_voice_text_mobile",
            band: "Additional Fields",
            getListKey: "has_voice_text_mobile",
            searchKey: "has_voice_text_mobile",
            isWildCardFilter: false,
            fieldType: "select",
            hasWildCardFilter: false
        },
        {
            label: "Date Synced",
            paramKey: "survey_recommendations.synced_time",
            key: "synced_time",
            searchKey: "survey_recommendations.synced_time",
            isWildCardFilter: true,
            hasWildCardFilter: false,
            fieldType: "date",
            band: "Additional Fields"
        },
        {
            label: "created At",
            paramKey: "survey_recommendations.created_at",
            key: "created_at",
            searchKey: "survey_recommendations.created_at",
            isWildCardFilter: true,
            hasWildCardFilter: false,
            fieldType: "date",
            band: "Additional Fields"
        },
        {
            label: "Updated At",
            key: "updated_at",
            paramKey: "survey_recommendations.updated_at",
            searchKey: "survey_recommendations.updated_at",
            isWildCardFilter: true,
            hasWildCardFilter: false,
            fieldType: "date",
            band: "Additional Fields"
        }
    ];

    useEffect(() => {
        function mapFields(inputData) {
            const hiddenFields = [
                "assets.usefull_life_remaining",
                "assets.service_life",
                "assets.installed_year",
                "assets.client_asset_condition_id",
                "mobile_recomm_notes_temp"
            ];

            return Object.entries(inputData || {})?.flatMap(([groupKey, group]) =>
                (group.fields || [])?.flatMap(fieldObj =>
                    Object.entries(fieldObj || {})
                        ?.filter(([fieldKey]) => !hiddenFields?.includes(fieldKey))
                        ?.flatMap(([fieldKey, field]) => ({
                            label: field?.label || "",
                            key: fieldKey.split(".").pop(),
                            paramKey: `dynamic_fields.${field?.field_name}`,
                            band: groupKey,
                            getListKey: `dynamic_fields.${field?.field_name}`,
                            searchKey: `dynamic_fields.${field?.field_name}`,
                            isWildCardFilter: false,
                            fieldType: ["select", "custom_select"].includes(field?.field_type) ? "select" : (field?.field_type ?? "select"),
                            hasWildCardFilter: !["boolean", "date"].includes(field?.field_type)
                        }))
                )
            );
        }

        setFilterFields([
            ...mapFields(getMobileRecommendationTemplateResponse?.survey_recommendation_template.dynamic_fields),
            ...additionalFields,
            ...getAiFilterFields(aiFieldsConfig)
        ]);
    }, [aiFieldsConfig]);

    const handleFilterView = async (item, dropdownRef, isWildCardFilter) => {
        const { key, options = [], getListKey, paramKey, fetchMore = false } = item;
        const isDynamic = field => ["dynamic_fields"].includes(field?.split(".")?.[0]);
        const isAiFields = field => ["ai_flat_json"].includes(field?.split(".")?.[1]);
        if ((isWildCardFilter && showWildcard !== key) || (!isWildCardFilter && showDropdown !== key && dropdownRef?.current)) {
            dropdownRef.current?.scrollIntoView({ behavior: "smooth" });
            wrapperRef.current = dropdownRef.current;
        }
        if (isWildCardFilter) {
            setShowWildcard(showWildcard === key ? "" : key);
            setShowDropdown("");
        } else if (!fetchMore) {
            setShowDropdown(showDropdown === key ? "" : key);
            setShowWildcard("");
        }
        if (!options?.length && (key !== showDropdown || fetchMore) && !isWildCardFilter) {
            const { filters = {}, ...rest } = { ...filterData };
            let { [paramKey]: omit, ...restList } = rest;
            let {
                "users.name": userName,
                "survey_recommendations.status": status,
                converted_or_not_converted,
                "survey_recommendations.recommendation_source_type": source_type,
                has_new_val: withWithoutField,
                "survey_recommendations.survey_type": survey_type,
                "survey_recommendations.has_media_issue": hasMediaIssue,
                "survey_recommendations.resync": resync,
                "survey_recommendations.analyze_status": analyze_status,
                ...omittedList
            } = restList;
            const filteredWildCardData = Object.entries(filters || {}).reduce((result, [key, elem]) => {
                if (
                    elem?.filters?.[0] &&
                    ((elem?.filters?.[0] !== "in_between" && elem?.key?.trim().length) ||
                        (elem?.filters?.[0] === "in_between" && elem?.key.from && elem.key.to) ||
                        elem?.filters?.[0] === "null" ||
                        elem.filters?.[0] === "not_null" ||
                        elem?.filters?.[0] === "count_gt_1")
                ) {
                    result[key] = elem;
                }
                return result;
            }, {});
            // Voice keys must not reach list / filters / jsonb_filters; they go into addon_filters below
            const { list: nonVoiceList, filters: wildCardData, addon: voiceAddon } = splitVoiceFilters({
                list: omittedList,
                filters: filteredWildCardData
            });
            omittedList = nonVoiceList;
            const {
                "survey_recommendations.created_at": createdAt = "",
                "survey_recommendations.updated_at": updatedAt = "",
                "users.name": surveyor = "",
                "survey_recommendations.recommendation_source_type": sourceType = "",
                "survey_recommendations.code": surveyCode = "",
                "survey_recommendations.synced_time": syncedAt = "",
                "survey_recommendations.surveyed_time": surveyedTime = "",
                "survey_recommendations.analyze_status": analyzeStatus = ""
            } = wildCardData ?? {};
            const keysToRemove = [
                "survey_recommendations.created_at",
                "survey_recommendations.updated_at",
                "users.name",
                "survey_recommendations.recommendation_source_type",
                "survey_recommendations.code",
                "survey_recommendations.synced_time",
                "survey_recommendations.surveyed_time",
                "survey_recommendations.analyze_status"
            ];

            const cleanedData = Object.fromEntries(Object.entries(wildCardData ?? {})?.filter(([key]) => !keysToRemove?.includes(key)));
            const search_filters = {};
            const jsonb_search_filters = {};
            if (item?.search) {
                if (isDynamic(paramKey) || isAiFields(paramKey)) {
                    jsonb_search_filters[paramKey] = { key: item?.search ?? "" };
                } else {
                    search_filters[paramKey] = { key: item?.search ?? "" };
                }
            }
            let formattedJsonBList = {};
            Object.keys(omittedList ?? {}).forEach(eachField => {
                if (isDynamic(eachField) || isAiFields(eachField)) {
                    formattedJsonBList = { ...(formattedJsonBList ?? {}), [eachField]: { value: omittedList?.[eachField] } };
                    const { [eachField]: omit, ...restList } = omittedList;
                    omittedList = { ...restList };
                }
            });
            let params = {
                field: getListKey,
                filters: {
                    ...surveyProjectFilterQuery,
                    ...(createdAt && { "survey_recommendations.created_at": createdAt }),
                    ...(updatedAt && { "survey_recommendations.updated_at": updatedAt }),
                    ...(sourceType && { "survey_recommendations.recommendation_source_type": sourceType }),
                    ...(surveyor && { "users.name": surveyor }),
                    ...(surveyCode && { "survey_recommendations.code": surveyCode }),
                    ...(syncedAt && { "survey_recommendations.synced_time": syncedAt }),
                    ...(surveyedTime && { "survey_recommendations.surveyed_time": surveyedTime }),
                    ...(analyzeStatus && { "survey_recommendations.analyze_status": analyzeStatus })
                },
                ...(Object.keys(cleanedData)?.length && { jsonb_filters: cleanedData }),
                ...(Object.keys(formattedJsonBList ?? {})?.length && { jsonb_list: formattedJsonBList }),
                search_filters: search_filters ?? {},
                jsonb_search_filters: jsonb_search_filters ?? {},
                list: {
                    users: currentFilters?.me ? { id: [currentFilters?.me] } : userName ? { name: userName } : undefined,
                    survey_recommendations: {
                        ...(status ? { status: status.map(e => parseInt(e)) } : {}),
                        ...(source_type ? { recommendation_source_type: source_type } : {}),
                        ...(withWithoutField ? { has_new_val: (withWithoutField ?? [])?.map(v => v?.includes("With new field")) } : {}),
                        ...(hasMediaIssue ? { has_media_issue: hasMediaIssue } : {}),
                        ...(survey_type ? { survey_type: survey_type } : {}),
                        ...(resync ? { resync: resync } : {}),
                        ...(analyze_status ? { analyze_status: analyze_status } : {})
                    }
                },
                converted_or_not_converted: converted_or_not_converted?.length === 2 ? undefined : converted_or_not_converted?.includes("converted"),
                // with_new_field: withWithoutField?.length === 2 ? undefined : withWithoutField?.includes("with_new_field"),
                ...(recommendationId ? recommendationId : {}),
                ...(!isSurveybasedRecoms ? { view_from: "projects" } : {}),
                ...(asset_id ? asset_id : {}),
                ...(currentFilters?.image_or_not ? { image_or_not: currentFilters?.image_or_not } : {})
            };
            params = {
                ...params,
                ...buildAddonFilters(!params?.list?.survey_recommendations?.status?.some(s => s === 5 || s === "5") && key !== "status", voiceAddon)
            };
            dispatch(actions.getMobileRecommendationFilterDropdown(params, key));
        }
    };

    const handleFilterUpdate = (item, data) => {
        setShowDropdown("");
        let updatedData = data;
        if (data?.length) {
            let options = masterFilterList[item.key];
            updatedData =
                item?.key === "status" || VOICE_FILTER_KEYS.includes(item?.key)
                    ? data.filter(item => {
                          return options?.list?.findIndex(opt => opt.id === item) !== -1;
                      })
                    : data.filter(item => options?.list?.findIndex(opt => opt.name === item) !== -1);
            const pickedNames = {};
            (options?.list ?? []).forEach(opt => {
                if (updatedData.includes(opt?.id) && opt?.id !== opt?.name) pickedNames[opt.id] = opt?.name ?? "";
            });
            if (Object.keys(pickedNames).length) dispatch(actions.setAppliedFilterNames(item.paramKey, pickedNames));
            setFilterData({ ...filterData, [item.paramKey]: updatedData });
        } else if (filterData.hasOwnProperty(item.paramKey)) {
            const updatedFilterData = { ...filterData };
            delete updatedFilterData[item.paramKey];
            setFilterData(updatedFilterData);
        }
    };

    const handleBandClick = (key, value) => {
        setShowBands({ ...showBands, [key]: value });
    };

    const handleSubmit = () => {
        let updatedFilterData = { ...filterData };
        const filteredWildCardData = Object.entries(updatedFilterData?.filters || {}).reduce((result, [key, elem]) => {
            if (
                (elem?.filters?.[0] !== "in_between" && elem?.key?.trim().length) ||
                (elem?.filters?.[0] === "in_between" && elem?.key.from && elem.key.to) ||
                elem.filters[0] === "null" ||
                elem.filters[0] === "not_null"
            ) {
                result[key] = elem;
            }
            return result;
        }, {});

        updatedFilterData = { ...updatedFilterData, filters: filteredWildCardData };
        setInitialFilterData(updatedFilterData);
        onCancel();
        submitMasterFilter(updatedFilterData);
    };

    const isDirty = !isEqual(filterData, initialFilterData);

    const handleCancel = () => {
        isDirty ? setShowCancelConfirmation(true) : onCancel();
    };

    const onCancelConfirm = () => {
        onCancel();
    };

    const clearAllFilters = () => {
        setFilterData({});
    };

    const getFilterCountByBand = band => {
        if (!(filterData && Object.keys(filterData)?.length)) {
            return 0;
        }
        let bandFilterCount = 0;
        const bandParamKeys = filterFields.filter(elem => elem.band === band).map(field => field.paramKey) || [];
        bandFilterCount = Object.keys(filterData).filter(key => bandParamKeys.includes(key) && filterData[key]?.length).length;

        if (filterData.filters && Object.keys(filterData.filters).length) {
            Object.entries(filterData.filters).forEach(([key, value]) => {
                if (bandParamKeys.includes(key) && (value.key || value.filters.includes("null") || value.filters.includes("not_null"))) {
                    bandFilterCount += 1;
                }
            });
        }

        return bandFilterCount;
    };

    const setWildCardFilter = (elem, data, checked) => {
        let currentFilter = filterData?.filters || {};
        if (data) {
            currentFilter = {
                ...currentFilter,
                [elem.paramKey]: data
            };
        } else {
            if (currentFilter?.hasOwnProperty(elem.paramKey)) {
                delete currentFilter[elem.paramKey];
            }
        }
        setFilterData({ ...filterData, filters: currentFilter });
    };

    const getTotalFilterCount = () => {
        let filterCount = 0;
        const { filters = {}, ...rest } = filterData;
        filterCount = getAppliedWildCardFilterCount(filters) + getAppliedListFilterCount(rest);
        return filterCount;
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleClickOutside = event => {
        if (wrapperRef?.current && !wrapperRef.current.contains(event.target)) {
            setShowDropdown("");
        }
    };

    const toggleFavorite = field => {
        if (favorites.includes(field)) {
            if (activeTab === "favorites") {
                setFavoriteRemoveConfirmation(true);
                setSelectFavoriteItem(field);
            } else {
                setFavorites(favorites.filter(item => item !== field));
            }
        } else {
            setFavorites([...favorites, field]);
        }
        favFilterRef.current = true;
    };

    useEffect(() => {
        if (favFilterRef.current) {
            saveFavorites();
        }
    }, [favorites]);

    useEffect(() => {
        // 0 Asset Filter 1 Dashboard 2 Recommendation 3 Survey assets 4 Mobile Recommendations 6 & 7 groupedit mobile asset & recom  8-FCA surevy tenmplate
        const params = {
            user_id: localStorage.getItem("userId"),
            filter_key: "4"
        };
        dispatch(AssetAction.getFavorites(params));
    }, []);

    useEffect(() => {
        setFavorites(favoriteFilters?.data?.properties || []);
    }, [favoriteFilters]);

    const saveFavorites = () => {
        let params = {
            user_id: localStorage.getItem("userId"),
            properties: JSON.stringify(favorites),
            filter_key: "4"
        };
        AssetAction.saveFavorites(params);
    };

    const onFavoriteRemoveConfirmation = () => {
        setFavoriteRemoveConfirmation(false);
        if (favorites.includes(selectedFavoriteItem)) {
            setFavorites(favorites.filter(item => item !== selectedFavoriteItem));
            favFilterRef.current = true;
        }
    };
    let AssetSurveyBands = [];

    Object.entries(getMobileRecommendationTemplateResponse?.survey_recommendation_template?.dynamic_fields || {})
        .sort(([, a], [, b]) => a.index - b.index)
        .map(([groupKey, group]) => {
            if (!AssetSurveyBands.includes(groupKey)) {
                AssetSurveyBands = [...AssetSurveyBands, groupKey];
            }
            return AssetSurveyBands;
        });
    AssetSurveyBands.push("Additional Fields");

    return {
        showBands,
        showDropdown,
        showWildcard,
        setShowWildcard,
        handleFilterView,
        masterFilterList,
        appliedFilterNames,
        handleBandClick,
        filterData,
        handleFilterUpdate,
        isDirty,
        handleSubmit,
        activeTab,
        setActiveTab,
        showCancelConfirmation,
        onCancelConfirm,
        setShowCancelConfirmation,
        handleCancel,
        filterFields,
        clearAllFilters,
        getFilterCountByBand,
        setShowDropdown,
        setWildCardFilter,
        fieldSearch,
        setFieldSearch,
        showAppliedFilters,
        setShowAppliedFilters,
        totalFilterCount: getTotalFilterCount(),
        favorites,
        toggleFavorite,
        showWildcardField,
        setWildcardField,
        showFavoriteRemoveConfirmation,
        setFavoriteRemoveConfirmation,
        onFavoriteRemoveConfirmation,
        AssetSurveyBands
    };
};
export default useMyFilters;
